import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import EmailSettingsForm from './components/EmailSettingsForm';
import EmailLogView from './components/EmailLogView';
import { getEmailLogs, deleteEmailLog, getEmailStats, getEmailSettings, updateEmailSettings } from './api/emailApi';
import Alert from '../../components/Alert/Alert';

const EmailConfiguration = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [activeTab, setActiveTab] = useState('settings');
  const [alert, setAlert] = useState(null);
  const [emailSettings, setEmailSettings] = useState(null);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (activeTab === 'settings') {
      loadEmailSettings();
    } else if (activeTab === 'logs') {
      loadEmailLogs();
      loadEmailStats();
    }
  }, [activeTab]);

  const loadEmailLogs = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: 0,
        size: 50,
        ...filterData
      };
      
      const response = await getEmailLogs(params);
      console.log('Email Logs API Response:', response);
      
      let data = (response.content || []).map(item => ({
        ...item,
        id: item.id,
        subject: item.subject || '',
        recipient: item.recipient || '',
        status: item.status || '',
        timestamp: item.createdAt || item.timestamp || '',
        errorMessage: item.errorMessage || '',
      }));
      
      console.log('Processed email logs data:', data);
      setEmailLogs(data);
    } catch (error) {
      console.error('Error loading email logs:', error);
      setEmailLogs([]);
      setAlert({ type: 'danger', message: 'Failed to load email logs.' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmailStats = async () => {
    try {
      const stats = await getEmailStats();
      setEmailStats(stats);
    } catch (error) {
      console.error('Error loading email stats:', error);
    }
  };

  const loadEmailSettings = async () => {
    setIsSettingsLoading(true);
    try {
      const settings = await getEmailSettings();
      console.log('Loaded email settings:', settings);
      setEmailSettings(settings);
    } catch (error) {
      console.error('Error loading email settings:', error);
      if (error.response?.status === 404) {
        // Settings belum ada, ini normal untuk pertama kali
        setEmailSettings(null);
      } else {
        setAlert({ type: 'danger', message: 'Failed to load email settings.' });
      }
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Filter email logs based on search query
  const filteredEmailLogs = emailLogs.filter((log) => {
    const query = searchQuery.toLowerCase();
    return (
      log.subject?.toLowerCase().includes(query) ||
      log.recipient?.toLowerCase().includes(query) ||
      log.status?.toLowerCase().includes(query) ||
      log.timestamp?.toLowerCase().includes(query)
    );
  });

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      status: '',
      recipient: ''
    });
  };

  const handleFilterApply = () => {
    loadEmailLogs();
  };

  const emailLogColumns = [
    { key: 'subject', label: 'Subject' },
    { key: 'recipient', label: 'Recipient' },
    { key: 'status', label: 'Status' },
    { key: 'timestamp', label: 'Time Stamp' },
  ];

  const handleSettingsSubmit = async (formData) => {
    setIsSaving(true);
    try {
      console.log('Saving email settings:', formData);
      
      // Jika password kosong dan ada settings yang sudah ada, jangan update password
      let settingsData = {
        emailProtocol: formData.emailProtocol,
        sendFromAddress: formData.sendFromAddress,
        smtpHost: formData.smtpHost,
        smtpUser: formData.smtpUser,
        smtpPort: parseInt(formData.smtpPort) || 587,
        companyName: formData.companyName,
        isActive: true
      };
      
      // Hanya update password jika diisi
      if (formData.smtpPassword && formData.smtpPassword.trim() !== '') {
        settingsData.smtpPassword = formData.smtpPassword;
      }
      
      console.log('Settings data to send:', settingsData);
      
      await updateEmailSettings(settingsData);
      await loadEmailSettings();
      
      setIsModalOpen(false);
      setAlert({ type: 'success', message: 'Email settings saved successfully!' });
    } catch (error) {
      console.error('Error saving email settings:', error);
      setAlert({ 
        type: 'danger', 
        message: 'Failed to save email settings: ' + (error.response?.data?.message || error.message) 
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Email log handlers
  const handleView = (row) => {
    setSelectedLog(row);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (window.confirm('Are you sure you want to delete this email log?')) {
      try {
        await deleteEmailLog(row.id);
        await loadEmailLogs();
        setAlert({ type: 'success', message: 'Email log deleted successfully!' });
      } catch (error) {
        console.error('Error deleting email log:', error);
        setAlert({ type: 'danger', message: 'Failed to delete email log.' });
      }
    }
  };

  return (
    <>
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}
      
      <div className="container-xl">
        <div className="row g-2 align-items-center">
          <div className="col">
            <h3 className="page-title m-0">Email Configuration</h3>
          </div>
        </div>

        <EmailSettingsForm
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSettingsSubmit}
          initialData={emailSettings} // Tambahkan ini!
          isLoading={isSettingsLoading}
        />

        <EmailLogView
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedLog(null);
          }}
          data={selectedLog}
        />

        <div className="page-body">
          <div className="container-xl">
            <div className="col">
              <div className="card">
                <div className="card-header">
                  <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                    <li className="nav-item-tab" role="presentation">
                      <button
                        className={`nav-link${activeTab === 'settings' ? ' active' : ''}`}
                        onClick={() => setActiveTab('settings')}
                        type="button"
                        role="tab"
                      >
                        Email Settings
                      </button>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <button
                        className={`nav-link${activeTab === 'logs' ? ' active' : ''}`}
                        onClick={() => setActiveTab('logs')}
                        type="button"
                        role="tab"
                      >
                        Email Logs
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  <div className="tab-content">
                    <div className={`tab-pane${activeTab === 'settings' ? ' active show' : ''}`} id="tabs-email-settings" role="tabpanel">
                      {activeTab === 'settings' && (
                        <div className="tab-pane active show">
                          {isSettingsLoading ? (
                            <div className="text-center py-4">
                              <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          ) : (
                            <div className="row">
                              <div className="col-12">
                                <div className="card">
                                  <div className="card-header">
                                    <h3 className="card-title">Email Configuration</h3>
                                    <div className="card-actions">
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsModalOpen(true)}
                                      >
                                        {emailSettings ? 'Edit Settings' : 'Configure Email'}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="card-body">
                                    {emailSettings ? (
                                      <div className="row">
                                        <div className="col-md-6">
                                          <p><strong>Protocol:</strong> {emailSettings.emailProtocol}</p>
                                          <p><strong>SMTP Host:</strong> {emailSettings.smtpHost}</p>
                                          <p><strong>SMTP Port:</strong> {emailSettings.smtpPort}</p>
                                          <p><strong>From Address:</strong> {emailSettings.sendFromAddress}</p>
                                        </div>
                                        <div className="col-md-6">
                                          <p><strong>Company Name:</strong> {emailSettings.companyName}</p>
                                          <p><strong>Status: </strong> 
                                            <span className={`badge ${emailSettings.isActive ? 'bg-success text-white' : 'bg-secondary text-white'}`}>
                                              {emailSettings.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-center py-4">
                                        <p>No email settings configured yet.</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`tab-pane${activeTab === 'logs' ? ' active show' : ''}`} id="tabs-email-logs" role="tabpanel">
                      {activeTab === 'logs' && (
                        <>
                          {/* Email Stats */}
                          <div className="row mb-3">
                            <div className="col-md-3">
                              <div className="card card-sm">
                                <div className="card-body">
                                  <div className="row align-items-center">
                                    <div className="col-auto">
                                      <span className="bg-primary text-white avatar">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                          <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z" />
                                          <path d="M3 7l9 6l9 -6" />
                                        </svg>
                                      </span>
                                    </div>
                                    <div className="col">
                                      <div className="font-weight-medium">
                                        {emailStats.total || 0}
                                      </div>
                                      <div className="text-muted">
                                        Total Emails
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Add more stats cards for sent, failed, pending, queued if desired */}
                            </div>
                          </div>
                          <GenericView
                            data={filteredEmailLogs}
                            columns={emailLogColumns}
                            onView={handleView}
                            onDelete={handleDelete}
                            isLoading={isLoading}
                            filterComponent={
                              <FilterDropdown
                                filterData={filterData}
                                onFilterChange={handleFilterChange}
                                onReset={handleFilterReset}
                                onApply={handleFilterApply}
                              />
                            }
                            searchComponent={
                              <SearchInput
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search email logs..."
                              />
                            }
                            actionButtons={
                              <ActionButtons
                                onExportExcel={() => console.log('Export Email Logs Excel')}
                                onExportPDF={() => console.log('Export Email Logs PDF')}
                              />
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailConfiguration; 